import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import Picker from '../../../common/components/picker/picker';
import { resolveProject, searchProjects } from '../../projects/contexts/projects';
import { searchUsers } from '../../users/contexts/users';
import { useCreateTicket } from '../contexts/tickets';
import { TICKET_KINDS } from '../utils/tickets';

import '../styles/forms.css';
import '../../clients/styles/form.css';

// Registrar lo que alguien reportó.
//
// LA PALABRA ES «REGISTRAR» Y NO «CREAR», y la diferencia es el tier entero.
// Quien completa esto no está abriendo un caso ni declarando un defecto: está
// dejando por escrito lo que otra persona dijo. Por eso el ticket nace
// `recibido` y sin clasificar, y por eso el formulario no tiene ni un campo
// para el veredicto — decir qué es viene después, con autor y fecha.
//
// QUIÉN REPORTÓ ES OBLIGATORIO, DE UNA DE DOS FORMAS. O es una persona de
// Mastermind (`reported_by`), o es alguien de afuera y se anota a quién
// representa quien lo carga (`reporter_ref`). El backend rechaza que falten las
// dos: un reporte sin dueño no se le puede contestar a nadie cuando se resuelva.
//
// EL ENTORNO NO ESTÁ ACÁ, y es una ausencia decidida. `environment_id` existe en
// el contrato, pero no hay ninguna ruta que liste los entornos DE UN PROYECTO:
// la única es `GET /admin/repositories/{id}/environments`, así que ofrecerlo
// significaría listar los repositorios del proyecto y después los entornos de
// cada uno, una consulta por repositorio, para llenar un campo opcional. Queda
// anotado como pendiente del backend. La versión sí se puede escribir, que es
// el dato por el que se pregunta primero cuando algo falla.

const TITLE_MAX = 200;
const BODY_MAX = 20000;
const VERSION_MAX = 60;
const REF_MAX = 200;

const INTERNAL = 'interno';
const EXTERNAL = 'externo';

export default function CreateTicketForm() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const createTicket = useCreateTicket();

    const [values, setValues] = useState(() => ({
        project_id: params.get('project') || '',
        kind: 'problema',
        title: '',
        body: '',
        version: '',
        source: INTERNAL,
        reported_by: '',
        reporter_ref: '',
    }));
    const [errors, setErrors] = useState({});

    const change = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
        setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const submit = async (e) => {
        e.preventDefault();

        const found = {};
        if (!values.title.trim()) found.title = 'El título es requerido';
        if (!values.body.trim()) found.body = 'Lo que se reportó es requerido';
        if (values.source === INTERNAL && !values.reported_by) {
            found.reported_by = 'Elegí quién reportó';
        }
        if (values.source === EXTERNAL && !values.reporter_ref.trim()) {
            found.reporter_ref = 'Decí a quién representás';
        }
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }

        const payload = {
            kind: values.kind,
            title: values.title.trim(),
            body: values.body.trim(),
        };
        if (values.project_id) payload.project_id = Number(values.project_id);
        if (values.version.trim()) payload.version = values.version.trim();
        if (values.source === INTERNAL) payload.reported_by = Number(values.reported_by);
        else payload.reporter_ref = values.reporter_ref.trim();

        try {
            await createTicket.mutateAsync(payload);
            toast.success('Reporte registrado');
            const back = values.project_id
                ? `/admin/operation?project=${values.project_id}`
                : '/admin/operation';
            navigate(back, { replace: true });
        } catch (err) {
            if (err.fields) setErrors(err.fields);
            else toast.error(err.message || 'No se pudo registrar el reporte');
        }
    };

    return (
        <section className="client-form">
            <header className="client-form__header">
                <h2 className="client-form__title">Registrar un reporte</h2>
                <p className="client-form__subtitle">
                    Lo que alguien dijo, tal como lo dijo. Queda <strong>recibido</strong> y sin
                    clasificar: registrarlo no confirma un error ni aprueba una sugerencia.
                </p>
            </header>

            <form className="client-form__card" onSubmit={submit}>
                <div className="client-form__grid">
                    <label className="client-form__field">
                        <span className="client-form__label">Proyecto</span>
                        <Picker
                            value={values.project_id}
                            onChange={(id) => change('project_id', id)}
                            fetchOptions={searchProjects}
                            resolveOption={resolveProject}
                            cacheKey="projects"
                            labelOf={project => project.name}
                            subtitleOf={project => project.slug}
                            placeholder="Elegir…"
                            emptyLabel="Sin proyecto"
                        />
                    </label>

                    <label className="client-form__field">
                        <span className="client-form__label">
                            Versión <span className="ticket-form__optional">— si se sabe</span>
                        </span>
                        <input
                            className="client-form__input client-form__input--mono"
                            value={values.version}
                            maxLength={VERSION_MAX}
                            placeholder="la que estaba usando"
                            onChange={e => change('version', e.target.value)}
                        />
                    </label>
                </div>

                {/* Un ticket sin proyecto solo lo puede cargar quien alcanza
                    todos los proyectos: si no, nadie acotado lo podría ver
                    después, ni siquiera quien lo escribió. El backend lo
                    rechaza; decirlo antes evita el viaje. */}
                {!values.project_id && (
                    <p className="client-form__note">
                        Sin proyecto, el ticket solo lo puede registrar una cuenta que alcance
                        todos los proyectos — si no, quedaría fuera del alcance de todo el
                        mundo, incluido vos.
                    </p>
                )}

                <fieldset className="ticket-kind">
                    <legend className="client-form__label">Tipo</legend>
                    {/* Un dato, no un disparador: ni `problema` ni `sugerencia`
                        abren un incidente ni una tarea. Sirve para buscar. */}
                    <div className="ticket-kind__options">
                        {TICKET_KINDS.map(item => (
                            <label key={item.key} className="ticket-kind__option">
                                <input
                                    type="radio"
                                    name="kind"
                                    value={item.key}
                                    checked={values.kind === item.key}
                                    onChange={() => change('kind', item.key)}
                                />
                                <span>{item.label}</span>
                            </label>
                        ))}
                    </div>
                    <p className="client-form__note">
                        Es un dato para poder buscarlo después. Una sugerencia no se convierte
                        en incidente por marcarla así, y un problema no abre una tarea.
                    </p>
                </fieldset>

                <label className="client-form__field client-form__field--wide">
                    <span className="client-form__label">
                        Título <span className="client-form__required">*</span>
                    </span>
                    <input
                        className={'client-form__input' + (errors.title ? ' client-form__input--invalid' : '')}
                        value={values.title}
                        maxLength={TITLE_MAX}
                        placeholder="Qué pasó, en una línea"
                        onChange={e => change('title', e.target.value)}
                    />
                    {errors.title && <span className="client-form__error">{errors.title}</span>}
                </label>

                <label className="client-form__field client-form__field--wide">
                    <span className="client-form__label">
                        Lo que reportó <span className="client-form__required">*</span>
                    </span>
                    <textarea
                        className={'client-form__input client-form__input--area' + (errors.body ? ' client-form__input--invalid' : '')}
                        value={values.body}
                        maxLength={BODY_MAX}
                        rows={6}
                        placeholder="Con sus palabras. Lo que se pierde al resumir es justo lo que sirve para reproducirlo."
                        onChange={e => change('body', e.target.value)}
                    />
                    {errors.body && <span className="client-form__error">{errors.body}</span>}
                </label>

                <fieldset className="ticket-kind">
                    <legend className="client-form__label">
                        Quién reportó <span className="client-form__required">*</span>
                    </legend>
                    <div className="ticket-kind__options">
                        <label className="ticket-kind__option">
                            <input
                                type="radio"
                                name="source"
                                checked={values.source === INTERNAL}
                                onChange={() => change('source', INTERNAL)}
                            />
                            <span>Alguien de Mastermind</span>
                        </label>
                        <label className="ticket-kind__option">
                            <input
                                type="radio"
                                name="source"
                                checked={values.source === EXTERNAL}
                                onChange={() => change('source', EXTERNAL)}
                            />
                            <span>Alguien de afuera</span>
                        </label>
                    </div>

                    {values.source === INTERNAL ? (
                        <label className="client-form__field client-form__field--wide">
                            <span className="client-form__label">La persona</span>
                            <Picker
                                value={values.reported_by}
                                onChange={(id) => change('reported_by', id)}
                                fetchOptions={searchUsers}
                                cacheKey="users"
                                labelOf={user => user.name}
                                subtitleOf={user => user.email}
                                placeholder="Buscar…"
                                invalid={!!errors.reported_by}
                            />
                            {errors.reported_by && (
                                <span className="client-form__error">{errors.reported_by}</span>
                            )}
                        </label>
                    ) : (
                        <label className="client-form__field client-form__field--wide">
                            <span className="client-form__label">A quién representás</span>
                            <input
                                className={'client-form__input' + (errors.reporter_ref ? ' client-form__input--invalid' : '')}
                                value={values.reporter_ref}
                                maxLength={REF_MAX}
                                placeholder="el cliente, el área, la persona que lo dijo"
                                onChange={e => change('reporter_ref', e.target.value)}
                            />
                            {errors.reporter_ref && (
                                <span className="client-form__error">{errors.reporter_ref}</span>
                            )}
                            <span className="client-form__note">
                                Mientras no haya una API pública, los reportes de afuera entran
                                por acá y quedan a nombre tuyo como quien los cargó.
                            </span>
                        </label>
                    )}
                </fieldset>

                <div className="client-form__actions">
                    <Link to="/admin/operation" className="client-form__cancel">Cancelar</Link>
                    <button type="submit" className="client-form__submit" disabled={createTicket.isPending}>
                        {createTicket.isPending ? 'Registrando…' : 'Registrar'}
                    </button>
                </div>
            </form>
        </section>
    );
}
